import React, { useState } from 'react';
import { MoreVertical, Edit, Trash2 } from 'lucide-react';
import { handleApiError } from '../../../utils/errorUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { useModalState } from '../../../hooks/useModalState';

interface MessageActionMenuProps {
  messageId: string;
  messageText: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (messageId: string, newText: string) => void;
  onDelete: (messageId: string) => void;
  onOpenChange?: (open: boolean) => void;
}

export function MessageActionMenu({
  messageId,
  messageText,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onOpenChange,
}: MessageActionMenuProps) {
  const {
    isOpen: editOpen,
    open: openEdit,
    close: closeEdit,
    setOpen: setEditOpen,
  } = useModalState();
  const [editText, setEditText] = useState(messageText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = React.useCallback(async () => {
    if (!editText.trim()) {
      setError('Message cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onEdit(messageId, editText.trim());
      closeEdit();
      setEditText(messageText);
      setError(null);
    } catch (err) {
      setError(handleApiError(err, 'edit message'));
    } finally {
      setSaving(false);
    }
  }, [editText, messageId, messageText, onEdit, closeEdit]);

  const {
    isOpen: deleteConfirmOpen,
    open: openDeleteConfirm,
    close: closeDeleteConfirm,
    setOpen: setDeleteConfirmOpen,
  } = useModalState();

  const handleDelete = React.useCallback(async () => {
    openDeleteConfirm();
  }, [openDeleteConfirm]);

  const confirmDelete = React.useCallback(async () => {
    closeDeleteConfirm();
    try {
      await onDelete(messageId);
      setError(null);
    } catch (err) {
      setError(handleApiError(err, 'delete message'));
    }
  }, [closeDeleteConfirm, messageId, onDelete]);

  const handleEditOpen = React.useCallback(() => {
    setEditText(messageText);
    setError(null);
    openEdit();
  }, [messageText, openEdit]);

  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const handleOpenChange = React.useCallback((open: boolean) => {
    setDropdownOpen(open);
    onOpenChange?.(open);
  }, [onOpenChange]);

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          {canEdit && (
            <DropdownMenuItem onSelect={handleEditOpen}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem onSelect={handleDelete} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Message</DialogTitle>
            <DialogDescription>Update your message text below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-message">Message</Label>
              <Textarea
                id="edit-message"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving || !editText.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Message</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteConfirm}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
